/**
 * How a clip arrives and leaves.
 *
 * An animation is a function of where the playhead is inside the clip, and
 * nothing else: no timers, no state, so scrubbing backwards through a fade
 * shows exactly the frame that playing forwards would. It is applied inside
 * renderFrame as a change of alpha, position and scale on top of the clip's
 * own transform, which is why the same handful of moves works for a title, a
 * logo and a picture-in-picture alike.
 */

import { clipDuration, type Clip } from "./timeline.ts";

export type Motion =
  | "none"
  | "fade"
  | "slide-up"
  | "slide-down"
  | "slide-left"
  | "slide-right"
  | "zoom"
  | "pop";

export interface ClipAnimation {
  in: Motion;
  out: Motion;
  /** Seconds. */
  inDuration: number;
  outDuration: number;
}

export const NO_ANIMATION: ClipAnimation = { in: "none", out: "none", inDuration: 0.5, outDuration: 0.5 };

export const MOTIONS: { value: Motion; label: string; hint: string }[] = [
  { value: "none", label: "None", hint: "Cut straight in" },
  { value: "fade", label: "Fade", hint: "From transparent" },
  { value: "slide-up", label: "Slide up", hint: "In from below" },
  { value: "slide-down", label: "Slide down", hint: "In from above" },
  { value: "slide-left", label: "Slide left", hint: "In from the right" },
  { value: "slide-right", label: "Slide right", hint: "In from the left" },
  { value: "zoom", label: "Zoom", hint: "Grows into place" },
  { value: "pop", label: "Pop", hint: "Springs in a little past its size, then settles" },
];

/** How much of the frame a slide travels. */
const SLIDE_DISTANCE = 0.12;

export interface AnimationState {
  alpha: number;
  /** Fractions of the composition's width and height. */
  dx: number;
  dy: number;
  scale: number;
}

const STILL: AnimationState = { alpha: 1, dx: 0, dy: 0, scale: 1 };

const easeOutCubic = (p: number): number => 1 - Math.pow(1 - p, 3);
/** Overshoots to about 1.1 and comes back. */
const easeOutBack = (p: number): number => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2);
};

/**
 * Where a motion has got to, `p` being 0 at fully out and 1 at fully in. The
 * exit runs the same curve backwards, so a slide-up leaves upwards: what came
 * from below goes on in the direction it was travelling.
 */
function motionState(motion: Motion, p: number, leaving: boolean): AnimationState {
  const eased = motion === "pop" ? easeOutBack(p) : easeOutCubic(p);
  const away = 1 - eased;
  const direction = leaving ? -1 : 1;
  switch (motion) {
    case "none":
      return STILL;
    case "fade":
      return { ...STILL, alpha: eased };
    case "slide-up":
      return { alpha: Math.min(1, eased * 1.5), dx: 0, dy: direction * away * SLIDE_DISTANCE, scale: 1 };
    case "slide-down":
      return { alpha: Math.min(1, eased * 1.5), dx: 0, dy: -direction * away * SLIDE_DISTANCE, scale: 1 };
    case "slide-left":
      return { alpha: Math.min(1, eased * 1.5), dx: direction * away * SLIDE_DISTANCE, dy: 0, scale: 1 };
    case "slide-right":
      return { alpha: Math.min(1, eased * 1.5), dx: -direction * away * SLIDE_DISTANCE, dy: 0, scale: 1 };
    case "zoom":
      return { alpha: Math.min(1, easeOutCubic(p) * 1.5), dx: 0, dy: 0, scale: 0.6 + 0.4 * eased };
    case "pop":
      return { alpha: Math.min(1, p * 3), dx: 0, dy: 0, scale: Math.max(0.01, eased) };
  }
}

/**
 * The clip's animation state at timeline time `t`.
 *
 * Both ends are clamped to half the clip, so a one-second title with a
 * two-second fade in and out does not spend its whole life in both at once:
 * it fades in for half a second, then out for half a second.
 */
export function animationAt(clip: Clip, t: number): AnimationState {
  const animation = clip.animation;
  if (!animation || (animation.in === "none" && animation.out === "none")) return STILL;
  const length = clipDuration(clip);
  const local = t - clip.start;
  if (local < 0 || local > length) return STILL;

  const inFor = Math.min(Math.max(0, animation.inDuration), length / 2);
  const outFor = Math.min(Math.max(0, animation.outDuration), length / 2);

  if (animation.in !== "none" && inFor > 0 && local < inFor) {
    return motionState(animation.in, local / inFor, false);
  }
  if (animation.out !== "none" && outFor > 0 && local > length - outFor) {
    return motionState(animation.out, (length - local) / outFor, true);
  }
  return STILL;
}

export const isAnimated = (clip: Clip): boolean =>
  Boolean(clip.animation && (clip.animation.in !== "none" || clip.animation.out !== "none"));
